import React, { useCallback, useMemo, useState } from 'react'
import * as yup from 'yup'

import { notifyError, notifySuccess } from '../features/notificationsManager/Notify'
import { ChargePoint, useChargePointUpdateMutation } from '../redux/api/web-api'
import { getProductName } from '../services/getProductName'
import useText from '../hooks/useText'
import useRemoteControlAutoRefresh from '../hooks/useRemoteControlAutoRefresh'
import { MAX_NAME_LENGTH } from '../constants/InputsLength'

import ComponentTypeLabel, { SProtocolSpan, TypeContainer } from './ComponentTypeLabel'

interface IComponentProps {
    chargePoint: ChargePoint
    editable: boolean
}

export interface IFormValues {
    name: string
}

type TypeLabelProps = {
    id: string
    productId: number
    isOcpp?: boolean
} & React.HTMLProps<HTMLDivElement>

export const TypeLabel = ({ productId, isOcpp, ...rest }: TypeLabelProps) => {
    const text = useText()
    return (
        <TypeContainer {...rest}>
            <span>{getProductName(productId)}</span>
            {isOcpp && (
                <>
                    <span>•</span>
                    <SProtocolSpan>{isOcpp ? text('lblOcpp') : text('lblCapiField')}</SProtocolSpan>
                </>
            )}
        </TypeContainer>
    )
}

const ChargePointTypeLabel = ({ chargePoint, editable = false }: IComponentProps) => {
    const text = useText()
    const [edit, setEdit] = useState(false)

    const [updateChargePoint] = useChargePointUpdateMutation()

    const schema = useMemo(
        () =>
            yup.object().shape({
                name: yup.string().required().max(MAX_NAME_LENGTH, text('lblNameError')),
            }),
        [text]
    )

    const title = useMemo(() => getProductName(chargePoint.productId), [chargePoint.productId])

    const saveData = useCallback(
        (formData: IFormValues) => {
            try {
                updateChargePoint({
                    chargePoint: {
                        ...chargePoint,
                        name: formData.name,
                    },
                }).unwrap()
                notifySuccess(text('msgChargerUpdated'))
                setEdit(false)
            } catch (error) {
                notifyError(text('msgChargerFailedToUpdate'))
            }
        },
        [chargePoint, text, updateChargePoint]
    )

    const updatedChargePointData = useRemoteControlAutoRefresh(chargePoint?.id)
    const updatedChargePointStatus = updatedChargePointData?.chargePointStatus
    return (
        <>
            {chargePoint && (
                <ComponentTypeLabel
                    editable={editable}
                    name={chargePoint?.name}
                    schema={schema}
                    updatedChargePointStatus={updatedChargePointStatus}
                    saveData={saveData}
                    title={title}
                    chargePoint={chargePoint}
                    edit={edit}
                    setEdit={setEdit}
                />
            )}
        </>
    )
}

export default ChargePointTypeLabel
